import { apiSlice, APITAGS, ErrorLike, errorTransform, TemplateOrCalculation } from './api';
import { ProjectResponse } from '@ydistri/identity-sdk';
import { MeCollection } from '../swagger/collections-identity';
import {
  ApiOperationType,
  ApplicationConfigurationResponse,
  ApplicationDataResponse,
  ApplicationStatus,
  CalculationStatus,
  CalculationStatusResponse,
  CategoryConfigurationDefinitionResponse,
  HintResponse,
  TemplateResponse,
} from '@ydistri/api-sdk';
import {
  ApplicationConfigurationsCollection,
  ApplicationDataCollection,
  CategoryConfigurationDefinitionsCollection,
  HintsCollection,
  TemplatesCollection,
} from '../swagger/collections';
import {
  SignalApplicationStatusChanged,
  SignalConfigurationChanged,
  SignalTemplateStatusChanged,
} from '../signalr/signalrInterfaces';
import { signalrClient } from '../signalr/client';
import { store } from '../store';
import { constructRequestParams, getTags } from './apiLib';
import { apiCategoryInsights } from '../components/global/CategoryInsights/apiCategoryInsights';
import { setProjectInfo } from '../store/localStore';
import { getSelectedProjectShortName } from './apiHeader';

export interface ConfigurationResponseExtended extends CalculationStatusResponse {
  refresh: boolean;
}

const { TAGS, TAGS_ARRAY } = getTags('application');

export const apiApplication = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no request params
      getUserProjects: builder.query<ProjectResponse[], void>({
        queryFn: async () => {
          try {
            const { data } = await MeCollection.projectsList();
            setProjectInfo(data.data);
            return data;
          } catch (error) {
            // Returned, not thrown: a thrown error reaches the screen as its message alone, and the
            // no-project screen reads the server's messages from response.data.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
            return { error: errorTransform(error as ErrorLike) };
          }
        },
        providesTags: [TAGS.projects],
        serializeQueryArgs: ({ endpointName }) => {
          // user projects do not depend on project code, it is not necessary to include it in the cache key
          return endpointName;
        },
      }),
      getTemplates: builder.query<TemplateResponse[], string>({
        queryFn: async selectedProjectShortName => {
          const { data } = await TemplatesCollection.getTemplates(
            {},
            constructRequestParams({ projectShortName: selectedProjectShortName }),
          );
          return data;
        },
        // The argument is the project, so a list keeps its key whichever project is selected
        serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}-${queryArgs}`,
        providesTags: (result, error, arg) => [{ type: TAGS.templates, id: arg }],
        async onCacheEntryAdded(projectShortName, api) {
          try {
            await api.cacheDataLoaded;

            const listener = (signal: SignalTemplateStatusChanged) => {
              if (signal.projectSlug.toLowerCase() !== projectShortName.toLowerCase()) return;
              // eslint-disable-next-line no-console -- we want the output here
              console.log('listener! signal: ', signal);

              /* =============== NEW TEMPLATE ================ */
              const templates = api.getCacheEntry().data ?? [];
              if (!templates.find(t => t.id === signal.templateId)) {
                TemplatesCollection.getTemplate(
                  signal.templateId,
                  constructRequestParams({ projectShortName }),
                ).then(({ data }) => {
                  api.updateCachedData(draft => {
                    if (!draft.find(t => t.id === signal.templateId)) draft.push(data.data);
                  });
                });
              }

              /* =============== DELETED TEMPLATE ================ */
              if (signal.status === CalculationStatus.SoftDeleted) {
                api.updateCachedData(draft => {
                  // eslint-disable-next-line no-console -- we want the output here
                  console.log('Updating cached data...', signal.templateId);
                  const iToDelete = draft.findIndex(template => template.id === signal.templateId);
                  if (iToDelete >= 0) {
                    draft.splice(iToDelete, 1);
                  }
                });
              }
            };

            // React to isLocked property changes so they are reflected in the UI (TemplateSelect)
            // IsLocked is a configuration changes not status change, so we need a separate listener
            const configurationChangedListener = (signal: SignalConfigurationChanged) => {
              if (signal.projectSlug.toLowerCase() !== projectShortName.toLowerCase()) return;
              if (
                signal.entityType === 'CalculationConfiguration' &&
                signal.operationType === ApiOperationType.Patch
              ) {
                // eslint-disable-next-line no-console -- we want this output
                console.log('getTemplates: configurationChangedListener', signal);
                const changedTemplateId = signal.templateId;
                const updates = signal.updates;

                api.updateCachedData(draft => {
                  const template = draft.find(t => t.id === changedTemplateId);
                  if (template) {
                    Object.entries(updates).forEach(([_, update]) => {
                      if (update.fieldName === 'isLocked') {
                        const value = update.value;
                        if (value === '0' || value === '1') {
                          template[update.fieldName] = value === '1';
                        }
                      }
                    });
                  }
                });
              }
            };

            signalrClient.on('TemplateStatusChanged', listener);
            signalrClient.on('TemplateConfigurationChanged', configurationChangedListener);
            api.cacheEntryRemoved.then(() => {
              signalrClient.off('TemplateStatusChanged', listener);
              signalrClient.off('TemplateConfigurationChanged', configurationChangedListener);
            });
          } catch {
            // no-op in case cache entry was removed before data was loaded
          }
          await api.cacheEntryRemoved;
        },
      }),
      getApplicationData: builder.query<ApplicationDataResponse[], undefined>({
        queryFn: async () => {
          const { data } = await ApplicationDataCollection.applicationDataList();
          return data;
        },
        providesTags: () => [TAGS.applicationData],
      }),
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no request params
      getApplicationConfiguration: builder.query<ApplicationConfigurationResponse, void>({
        queryFn: async () => {
          const { data } =
            await ApplicationConfigurationsCollection.applicationConfigurationsList();
          return data;
        },
        providesTags: () => [TAGS.applicationData],
      }),
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no request params
      getApplicationHints: builder.query<HintResponse[], void>({
        queryFn: async () => {
          const { data } = await HintsCollection.hintsList();
          return data;
        },
        providesTags: () => [TAGS.applicationHints],
      }),
      getCategoryConfigurationDefinitions: builder.query<
        CategoryConfigurationDefinitionResponse[],
        // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no request params
        void
      >({
        queryFn: async () => {
          const { data } =
            await CategoryConfigurationDefinitionsCollection.categoryConfigurationDefinitionsList();

          return data;
        },
        providesTags: () => [TAGS.categoryConfigurationDefinitions],
      }),
      getStatuses: builder.query<ConfigurationResponseExtended, TemplateOrCalculation>({
        queryFn: async arg => {
          const { data } = await TemplatesCollection.getTemplateConfiguration(arg.id);
          return { ...data, data: { ...data.data, refresh: false } };
        },
        providesTags: () => [TAGS.templateConfiguration],
        async onCacheEntryAdded({ type: _, id: selectedTemplateId }, api) {
          const projectShortName = getSelectedProjectShortName();
          try {
            await api.cacheDataLoaded;

            const listener1 = (signal: SignalTemplateStatusChanged) => {
              if (
                signal.projectSlug !== projectShortName ||
                getSelectedProjectShortName() !== projectShortName
              )
                return;
              if (selectedTemplateId !== signal.templateId) return;
              api.updateCachedData(draft => {
                const { status } = signal;
                draft.templateStatus = status;
                if (status === CalculationStatus.Completed) {
                  store.dispatch(
                    apiCategoryInsights.util.invalidateTags([
                      APITAGS.categoryInsights.categoryInsights,
                    ]),
                  );
                }
                // eslint-disable-next-line no-console -- we want the output here
                console.log('SIGNAL TemplateStatusChanged', signal);
              });
            };

            const listener2 = (signal: SignalApplicationStatusChanged) => {
              if (
                signal.projectSlug !== projectShortName ||
                getSelectedProjectShortName() !== projectShortName
              )
                return;
              api.updateCachedData(draft => {
                const { applicationStatus, statusInfo } = signal;
                draft.applicationStatus = applicationStatus;
                draft.statusInfo = statusInfo;
                // eslint-disable-next-line no-console -- we want the output here
                console.log('SIGNAL ApplicationStatusChanged', signal);
                if (applicationStatus === ApplicationStatus.Opened) {
                  //refresh is set to true only through signalR and only when app status became "opened"
                  draft.refresh = true;
                }
              });
            };

            signalrClient.on('TemplateStatusChanged', listener1);
            signalrClient.on('ApplicationStatusChanged', listener2);

            api.cacheEntryRemoved.then(() => {
              signalrClient.off('TemplateStatusChanged', listener1);
              signalrClient.off('ApplicationStatusChanged', listener2);
            });
          } catch {
            // no-op in case cache entry was removed before data was loaded
          }
          await api.cacheEntryRemoved;
        },
      }),
    }),
  });

export const {
  useGetUserProjectsQuery,
  useGetTemplatesQuery,
  useGetApplicationDataQuery,
  useGetApplicationConfigurationQuery,
  useGetApplicationHintsQuery,
  useGetCategoryConfigurationDefinitionsQuery,
  useGetStatusesQuery,
} = apiApplication;
